import { Injectable, OnDestroy, Signal } from '@angular/core';
import { Subject } from 'rxjs';
import { AdobeEnum } from '../../enums/adobe.enum';
import { environment } from 'src/environments/environment';

export interface PageViewEvent {
  envWork: string;
  language: string;
  userCountry?: string;
  pageName: string;
  pageUrl?: string;
  pageCategory1?: string;
  pageCategory2?: string;
  pageCategory3?: string;
  pageCategory4?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdobeAnalyticsService implements OnDestroy {
  private readonly _unsubscribe$ = new Subject<boolean>();

  constructor() {}

  /**
   * Carga el script principal de Adobe Launch (solo una vez)
   */
  loadScript(adobeUrl: string): void {
    const scriptId = AdobeEnum.ADOBE_ELEMENT_ID;

    // Si ya está cargado, solo asegurar el DataLayer y salir
    if (document.getElementById(scriptId)) {
      console.debug('[AdobeAnalytics] Script ya cargado');
      this.ensureDataLayer();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = adobeUrl;
    script.async = true;

    script.onload = () => {
      console.info('[AdobeAnalytics] Script cargado correctamente');
      this.ensureDataLayer();
    };

    script.onerror = () => console.error('[AdobeAnalytics] Error al cargar el script');

    document.head.insertAdjacentElement('beforeend', script);
  }

  private ensureDataLayer(): void {
    const w = window as any;

    if (!w.adobeDataLayer) {
      this.initializeDataLayer();
    }
  }

  /**
   * Inicializa el DataLayer global si no existe
   */
  private initializeDataLayer(): void {
    const w = window as any;

    if (!w.adobeDataLayer) {
      w.adobeDataLayer = {};
      console.info('[AdobeAnalytics] DataLayer inicializado:', w.adobeDataLayer);
    }
  }

  sendPageView(data: Partial<PageViewEvent>): void {
    const w = window as any;
    this.ensureDataLayer();
    const eventData: PageViewEvent = {
      envWork: environment.production ? 'production' : 'development',
      language: localStorage.getItem('language') || 'fr',
      userCountry: data.userCountry,
      pageName: data.pageName || '',
      pageUrl: data.pageUrl || window.location.href,
      pageCategory1: data.pageCategory1,
      pageCategory2: data.pageCategory2,
      pageCategory3: data.pageCategory3,
      pageCategory4: data.pageCategory4
    };

    const filteredEventData = Object.fromEntries(
      Object.entries(eventData).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );

    //w.adobeDataLayer.push(filteredEventData);
    Object.assign(w.adobeDataLayer, filteredEventData);

    console.info('[AdobeAnalytics] PAGE VIEW enviado:', filteredEventData);
  }

  /**
   * Actualiza valores en el DataLayer
   */
  updateDataLayer(data: Record<string, any>): void {
    const w = window as any;
    this.ensureDataLayer();
    //w.adobeDataLayer.push(data);
    Object.assign(w.adobeDataLayer, data);
  }

  /**
   * Envía un evento personalizado a Adobe Launch
   */
  pushEvent(eventName: string, payload: Record<string, any> = {}): void {
    const w = window as any;
    this.ensureDataLayer();
    const eventData = { event: eventName, ...payload };
    //w.adobeDataLayer.push(eventData);
    Object.assign(w.adobeDataLayer, eventData);

    console.debug('[AdobeAnalytics] Evento enviado:', eventData);
  }

  /**
   * Eventos mobilier
   */

  sendMobilierData() {
    this.sendPageView({
      pageName: 'multiassistance/furniture/category-choice',
      pageCategory1: 'furniture',
      pageCategory2: 'product',
      pageCategory3: 'category'
    });
  }
  sendMobilierSeeAllData() {
    this.sendPageView({
      pageName: 'multiassistance/furniture/see-all',
      pageCategory1: 'furniture',
      pageCategory2: 'product',
      pageCategory3: 'see-all'
    });
  }

  sendMobilierProductData(literals: string[]) {
    this.sendPageView({
      pageName: 'multiassistance/furniture/product-selection',
      pageCategory1: 'furniture',
      pageCategory2: 'product',
      pageCategory3: 'selection',
      pageCategory4: literals.at(-1)
    });
  }

  sendUptecData() {
    this.sendPageView({
      pageName: 'multiassistance/furniture/buying-date',
      pageCategory1: 'furniture',
      pageCategory2: 'product',
      pageCategory3: 'buying-date'
    });
  }

  /**
   * Limpieza del servicio
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next(true);
    this._unsubscribe$.complete();
  }
}
